import { MAINLAND_US_BOUNDS_PADDED } from '@cibo/ui'
import { Stack, SxProps, ThemeProvider, useTheme } from '@mui/material'
import { Feature, FeatureCollection, featureCollection } from '@turf/turf'
import { MapLayerMouseEvent, MapboxGeoJSONFeature } from 'mapbox-gl'
import { flatten, propEq } from 'ramda'
import { FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import ReactMapGL, {
  Layer,
  LngLatBoundsLike,
  MapProps,
  MapRef,
  SkyLayer,
  Source,
} from 'react-map-gl/dist/es5'
//@ts-ignore
import AutoSizer from 'react-virtualized-auto-sizer'
import { parcelSearchFeatureBoundingBox } from '../../utils'
import { LayerInteractionContext, LayerInteractionProvider } from '../LayerInteractionContext'
import { BoundsTracker, LayerContext, LayerProvider } from './Layers'
import { MapGLViewport, ViewportContextProvider, useViewportContext } from './ViewportContext'
import { MAPBOX_STYLES, MAPBOX_TOKEN, themeForMapStyle } from './const'

let hoverFeatures: MapboxGeoJSONFeature[] | undefined

const STABLE_MOVE_EVENT_COUNT = 10
const DISABLE_MAPS = import.meta.env.VITE_DISABLE_MAPS

export interface MapGLProps extends MapProps {
  defaultLayers?: boolean
  focusFeatures?: FeatureCollection
  focusOnFeatureClick?: (e: MapLayerMouseEvent) => boolean | boolean
  initialViewport?: MapGLViewport
  layers?: ReactNode
  overlay?: ReactNode
  sx?: SxProps
  ['data-testid']?: string
}

const skyLayer: SkyLayer = {
  id: 'sky',
  type: 'sky',
  paint: {
    'sky-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0, 5, 0.3, 8, 1],
    'sky-type': 'atmosphere',
    'sky-atmosphere-sun': [180.52639874132626, 20.138711239426257],
    'sky-atmosphere-sun-intensity': 5,
  },
}

export const MapGL: FC<MapGLProps> = props => (
  <LayerProvider>
    <ViewportContextProvider initialViewport={props.initialViewport}>
      <LayerInteractionProvider>
        <MapGLHoverable {...props} />
      </LayerInteractionProvider>
    </ViewportContextProvider>
  </LayerProvider>
)

export const MapGLWithoutViewport: FC<MapGLProps> = props => (
  <LayerProvider>
    <LayerInteractionProvider>
      <MapGLHoverable {...props} />
    </LayerInteractionProvider>
  </LayerProvider>
)

const MapGLHoverable: FC<MapGLProps> = ({
  children,
  defaultLayers,
  focusFeatures,
  focusOnFeatureClick,
  interactiveLayerIds,
  initialViewport,
  layers,
  overlay,
  sx,
  ...mapGLProps
}) => {
  const [initializedViewport, setInitializedViewport] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapRef>(null)
  const { current: map } = mapRef
  const theme = useTheme()
  const { viewport, setViewport } = useViewportContext()

  const { mapStyle, showTerrain, terrainExaggeration } = useContext(LayerContext)

  const flyTo = useCallback(
    (features: FeatureCollection) => {
      try {
        if (container.current && map) {
          const [minLng, minLat, maxLng, maxLat] = parcelSearchFeatureBoundingBox(features)

          map?.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            {
              pitch: 0,
              bearing: 0,
              padding: 48,
            }
          )
        }
      } catch (err) {
        console.log(err)
        //report to Sentry?
      }
    },
    [map, container.current, viewport]
  )

  const recenter = () => {
    if (!!container.current && viewport && focusFeatures && focusFeatures.features.length > 0) {
      flyTo(focusFeatures)
    }
  }

  useEffect(() => {
    if (!initializedViewport && !!container.current && !!focusFeatures) {
      recenter()
      setInitializedViewport(true)
    }
  }, [container.current, focusFeatures, initializedViewport])

  useEffect(() => {
    if (!!focusFeatures) {
      recenter()
    }
  }, [focusFeatures])

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (map && container.current && focusOnFeatureClick && e.features) {
        typeof focusOnFeatureClick === 'function'
          ? focusOnFeatureClick(e) && flyTo(featureCollection([e.features[0]] as Feature[]))
          : focusOnFeatureClick && flyTo(featureCollection([e.features[0]] as Feature[]))
      }
      setClickedFeatures(e.features)

      //@ts-ignore the event types _should_ match but currently don't
      mapGLProps.onClick && mapGLProps.onClick(e)
    },
    [focusOnFeatureClick, mapGLProps.onClick, mapRef, container]
  )

  const onResize = useCallback(() => {
    if (map) {
      setTimeout(() => map?.resize(), 0)
    }
  }, [map])

  const { setClickedFeatures, setHoverFeatures, dwellFeature, setDwellFeature } =
    useContext(LayerInteractionContext)

  const handleMouseMove = useCallback(
    (e: MapLayerMouseEvent) => {
      if (map) {
        //show something is interactable
        map.getCanvas().style.cursor = e.features?.length ? 'pointer' : ''

        if (hoverFeatures) {
          hoverFeatures.forEach(f => !!f.id && map?.setFeatureState(f, { hover: false }))
        }

        if (e.features) {
          hoverFeatures = e.features
          e.features.forEach(f => !!f.id && map?.setFeatureState(f, { hover: true }))

          setHoverFeatures(e.features)
        }
      }
      //@ts-ignore mapbox event handler types do not match react-map-gl event handler types
      mapGLProps.onMouseMove && mapGLProps.onMouseMove(e)
    },
    [interactiveLayerIds, map, mapGLProps.onMouseMove]
  )

  const [hoverCounts, setHoverCounts] = useState<
    Record<string, { featureId?: number; count: number }>
  >({})

  useEffect(() => {
    if (!interactiveLayerIds?.length || !map) {
      return
    }

    const newHoverCounts: Record<string, { featureId?: number; count: number }> = {}

    hoverFeatures?.forEach(feature => {
      if (!feature?.id || !feature?.layer?.id) {
        return
      }
      const featureId = feature.id as number
      const layerId = feature.layer.id

      const oldHoverCount = hoverCounts && hoverCounts[layerId]

      newHoverCounts[layerId] = {
        featureId,
        count: oldHoverCount?.featureId === featureId ? oldHoverCount.count + 1 : 1,
      }
    })

    let newDwellFeatureId

    for (let i = 0, l = interactiveLayerIds.length; i < l; i++) {
      const hoverCount = newHoverCounts[interactiveLayerIds[i]]

      if (hoverCount?.count >= STABLE_MOVE_EVENT_COUNT) {
        newDwellFeatureId = hoverCount.featureId as number
      } else {
        break
      }
    }

    // @ts-ignore
    if (newDwellFeatureId !== dwellFeature?.id) {
      dwellFeature && map.setFeatureState(dwellFeature, { dwell: false })

      // @ts-ignore
      const newDwellFeature = hoverFeatures.find(propEq('id', newDwellFeatureId))
      if (newDwellFeature) {
        map.setFeatureState(newDwellFeature, { dwell: true })
        setDwellFeature(newDwellFeature)
      }
    }

    setHoverCounts(newHoverCounts)
  }, [hoverFeatures, interactiveLayerIds, map])

  return (
    <Stack
      sx={{ flex: 1, ...sx }}
      direction="row"
      ref={container}
      data-testid={mapGLProps['data-testid']}
    >
      {/* @ts-ignore previously hidden error unrelated to PR */}
      <AutoSizer>{onResize}</AutoSizer>
      <ThemeProvider theme={themeForMapStyle(theme, mapStyle)}>
        <ReactMapGL
          ref={mapRef}
          initialViewState={initialViewport}
          {...viewport}
          onMove={({ viewState }) => setViewport(viewState)}
          style={{ flex: 1 }}
          //@ts-ignore
          mapStyle={MAPBOX_STYLES[mapStyle || 'SATELLITE'][theme.palette.mode]}
          mapboxAccessToken={!DISABLE_MAPS && MAPBOX_TOKEN}
          testMode={DISABLE_MAPS}
          maxZoom={18}
          minZoom={3}
          maxBounds={flatten(MAINLAND_US_BOUNDS_PADDED) as LngLatBoundsLike}
          maxPitch={80}
          //@ts-ignore mapbox event handler types do not match react-map-gl event handler types
          onMouseMove={handleMouseMove}
          interactiveLayerIds={interactiveLayerIds}
          {...mapGLProps}
          //@ts-ignore mapbox event handler types do not match react-map-gl event handler types
          onClick={handleClick}
          trackResize
          fog={showTerrain ? { range: [0.5, 10], color: 'white', 'horizon-blend': 0.1 } : undefined}
          terrain={
            showTerrain ? { source: 'mapbox-dem', exaggeration: terrainExaggeration } : undefined
          }
          boxZoom
          reuseMaps
        >
          <BoundsTracker />
          <Source
            id="mapbox-dem"
            type="raster-dem"
            url="mapbox://mapbox.mapbox-terrain-dem-v1"
            tileSize={512}
            maxzoom={14}
          />
          <Layer {...skyLayer} />

          {children}
        </ReactMapGL>
      </ThemeProvider>
    </Stack>
  )
}
